import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import * as adminService from '../../services/adminService';

interface ResetPasswordDialogProps {
  userId: string;
  userName: string;
  onClose: () => void;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  userId,
  userName,
  onClose,
}) => {
  const { darkMode } = useTheme();
  const { showToast } = useToast();

  const [isResetting, setIsResetting] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReset = async () => {
    setIsResetting(true);
    setError(null);
    try {
      const result = await adminService.resetPassword(userId);
      setTempPassword(result.temporary_password || result.password || result.temp_password);
      showToast('Password reset successfully', 'success');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reset password');
      showToast('Failed to reset password', 'error');
    } finally {
      setIsResetting(false);
    }
  };

  const handleCopy = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      showToast('Password copied to clipboard', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast('Failed to copy password', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`relative w-full max-w-md p-6 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Reset Password
        </h2>

        {tempPassword ? (
          /* ── Success: show temporary password ── */
          <div>
            <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Password has been reset for <span className="font-medium">{userName}</span>. Share this temporary password with the user:
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={tempPassword}
                className={`flex-1 px-3 py-2 border rounded-md text-sm font-mono ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-gray-50 border-gray-300 text-gray-900'
                }`}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                onClick={handleCopy}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                  darkMode
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className={`text-xs mt-3 ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
              The user should change this password on their next login.
            </p>
            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* ── Confirmation state ── */
          <div>
            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100 text-sm">
                {error}
              </div>
            )}

            <p className={`mb-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Are you sure you want to reset the password for{' '}
              <span className="font-medium">{userName}</span>?
              This will generate a new temporary password and invalidate the current one.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={onClose}
                disabled={isResetting}
                className={`px-4 py-2 rounded-md ${
                  darkMode
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleReset}
                disabled={isResetting}
                className={`px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white ${
                  isResetting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isResetting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordDialog;
